import { CalendarMonth } from "@mui/icons-material";
import { MenuItem, Select } from "@mui/material/";
import { DesktopDatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format, parseISO, isValid as isValidDate, isBefore, isAfter } from "date-fns";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import useMedicalCompleteLock from "../../../../../../hooks/useMedicalCompleteLock";
import useRevalidateParent from "../../../../../../hooks/useRevalidateParent";
import axios from "../../../../../api/axios";
import ApiRequestErrorHandler from "../../../../../global/ApiRequestErrorHandler";
import FormField from "../../../../../global/FormComponents/field/FormField";
import Modal from "../../../../../global/Modal";
import ResponseModal from "../../../../../global/ResponseModal";
import { MEDICALLY_COMPLETE_LOCK_MESSAGE } from "../../../../../modules/ClientDetail/Case/utils/constants";
import { TextEditor } from "../../../../../ui library/components/text-editors/text-editor";
import { getCenturyDateBounds } from "../../../../../ui library/helpers/functions";
import { dateTimeStyles, selectStyles } from "../../constant";

export default function EditApptv2({
  selectedAppt,
  caseInfo,
  open,
  onClose,
  proCliTrigger,
  setProCliTrigger,
  onExpand,
}) {
  const revalidateParent = useRevalidateParent("rvClientDetail");
  const { isAppointmentLocked } = useMedicalCompleteLock();
  const appointmentLocked = isAppointmentLocked(caseInfo);

  const [openSuccess, setOpenSuccess] = useState(false);

  const [dateValue, setDateValue] = useState(null);
  const [timeValue, setTimeValue] = useState(null);
  const [hasDateError, setHasDateError] = useState(false);

  const [apptStatus, setApptStatus] = useState("");
  const [apptNotes, setApptNotes] = useState("");
  const [openError, setOpenError] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providerTypeList, setProviderTypeList] = useState([]);
  const [selectedProviderType, setSelectedProviderType] = useState("");
  const { min: minDate, max: maxDate } = useMemo(() => getCenturyDateBounds(), []);

  const toDate = (v) => {
    if (!v) {
      return null;
    }
    if (v instanceof Date) {
      return v;
    }
    try {
      const d = parseISO(String(v));
      return isValidDate(d) ? d : null;
    } catch {
      const d = new Date(v);
      return isValidDate(d) ? d : null;
    }
  };
  const fmt = (d, pattern) => (d && isValidDate(d) ? format(d, pattern) : "");

  useEffect(() => {
    if (!selectedAppt) {
      return;
    }

    const dFromSplit = toDate(selectedAppt?.appointmentDate);
    const tFromSplit = toDate(selectedAppt?.appointmentTime);

    setDateValue(dFromSplit);
    setHasDateError(false);
    setTimeValue(tFromSplit);

    setApptStatus(selectedAppt?.status ?? "");
    setApptNotes(selectedAppt?.notes ?? "");

    setSelectedProviderType(
      selectedAppt?.provider_type?.id ?? selectedAppt?.providerType?.id ?? ""
    );
  }, [selectedAppt]);

  const handleOpenSuccess = () => {
    setOpenSuccess(!openSuccess);
    setApptStatus("");
    setApptNotes("");
    setTimeout(() => {
      if (typeof setProCliTrigger === "function" && proCliTrigger !== undefined) {
        setProCliTrigger(!proCliTrigger);
      }
      onClose();
    });
    revalidateParent(); // Also revalidates children
  };

  const handleDateChange = (newValue) => {
    if (!newValue) {
      setDateValue(null);
      setHasDateError(false);
      return;
    }
    const d = new Date(newValue);
    if (isValidDate(d) && !isBefore(d, minDate) && !isAfter(d, maxDate)) {
      setDateValue(d);
      setHasDateError(false);
    } else {
      setDateValue(null);
      setHasDateError(true);
    }
  };

  const handleTimeChange = (newValue) => {
    if (!newValue) {
      return setTimeValue(null);
    }
    const d = new Date(newValue);
    setTimeValue(isValidDate(d) ? d : null);
  };

  const handleApptEdit = () => {
    if (appointmentLocked) {
      setErrorArray([MEDICALLY_COMPLETE_LOCK_MESSAGE]);
      setOpenError(true);
      return;
    }

    if (hasDateError) {
      setErrorArray(["Please correct the appointment date before saving."]);
      setOpenError(true);
      return;
    }

    setLoading(true);

    const datePatch = {
      ...(dateValue instanceof Date && isValidDate(dateValue)
        ? { appointment_date: format(dateValue, "yyyy-MM-dd") }
        : {}),
      ...(timeValue instanceof Date && isValidDate(timeValue)
        ? { appointment_time: format(timeValue, "HH:mm:ss") }
        : {}),
    };

    const patchedProcedure = {
      ...datePatch,
      ...(apptStatus === "" ? {} : { status: apptStatus }),
      ...(apptNotes === "" ? {} : { notes: apptNotes }),
      ...(selectedProviderType === "" ? {} : { provider_type: selectedProviderType }),
    };

    axios
      .patch(`/api/appointments/${selectedAppt.pk}/`, patchedProcedure, {})
      .then(function (response) {
        if (response.status < 405) {
          setOpenSuccess(!openSuccess);
          setLoading(false);
        }
      })
      .catch(function (error) {
        if (error.response) {
          const res = error.response.data;
          const errArr = ApiRequestErrorHandler(res);
          setErrorArray(errArr);
          setOpenError(!openError);
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    const fetchProviderTypes = async () => {
      try {
        if (!selectedAppt?.provider?.pk) {
          return;
        }
        const response = await axios.get(`/api/provider/${selectedAppt.provider.pk}/`, {});
        const results = response.data?.provider_types;
        if (results) {
          setProviderTypeList(results);
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (selectedAppt) {
      fetchProviderTypes();
    }

    return () => {
      setProviderTypeList([]);
      setSelectedProviderType("");
    };
  }, [selectedAppt]);

  useEffect(() => {
    if (providerTypeList.length) {
      const currentId = selectedAppt?.provider_type?.id ?? selectedAppt?.providerType?.id ?? "";
      if (currentId) {
        setSelectedProviderType(currentId);
      }
    }
  }, [providerTypeList, selectedAppt]);

  const onChangeHandlerEditNote = (newText) => setApptNotes(newText);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Appointment Details"
      actions={[
        {
          label: "Update Appointment Details",
          isLoading: loading,
          disabled: appointmentLocked,
          onClick: handleApptEdit,
        },
      ]}
      onExpand={onExpand}
      maxWidth="md"
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className="flex w-full flex-col gap-4">
          {!selectedAppt ? (
            "Please Select an Appointment to Continue"
          ) : (
            <>
              <div>
                <div className="text-lg font-bold underline">APPOINTMENT INFO</div>
                <div>
                  <span>Name:</span> <span>{selectedAppt?.provider?.name}</span>
                </div>
                <div>
                  <span>Type:</span> <span>{selectedAppt?.provider_type?.name}</span>
                </div>
                <div>
                  <span>Phone:</span> <span>{selectedAppt?.provider?.phone}</span>
                </div>

                <div>
                  <span>Date:</span>{" "}
                  <span>{fmt(toDate(selectedAppt?.appointmentDate), "MM/dd/yyyy")}</span>
                </div>
                <div>
                  <span>Time:</span>{" "}
                  <span>{fmt(toDate(selectedAppt?.appointmentTime), "hh:mm a")}</span>
                </div>
              </div>

              <div>
                <div className="text-lg font-bold underline">CLIENT INFO</div>
                <div>
                  <span>Name: </span> <span>{selectedAppt?.client?.name}</span>
                </div>
                <div>
                  <span>Email:</span> <span>{selectedAppt?.client?.email}</span>
                </div>
                <div>
                  <span>Phone:</span> <span>{selectedAppt?.client?.phone}</span>
                </div>
              </div>
            </>
          )}

          <div>
            <span className="text-lg font-bold underline">EDIT APPOINTMENT DETAILS</span>
            <div className="flex flex-col gap-4">
              <FormField label="Provider Type">
                <Select
                  className="w-full"
                  sx={{
                    ...selectStyles,
                    "& fieldset": { borderColor: "#9ca3af" },
                  }}
                  value={selectedProviderType}
                  onChange={(event) => setSelectedProviderType(event.target.value)}
                  placeholder="Please select a provider type"
                  displayEmpty
                  renderValue={(value) =>
                    !value ? (
                      <span className="text-gray-400">Please select a provider type</span>
                    ) : (
                      providerTypeList.find((p) => p.id === value)?.name
                    )
                  }
                >
                  {providerTypeList?.map((protypes) => (
                    <MenuItem value={protypes.id} key={protypes.id}>
                      {protypes.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormField>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Appointment Date">
                  <DesktopDatePicker
                    value={dateValue}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={handleDateChange}
                    onError={(reason) => setHasDateError(Boolean(reason))}
                    components={{ OpenPickerIcon: CalendarMonth }}
                    slotProps={{
                      textField: { fullWidth: true, sx: dateTimeStyles },
                      inputAdornment: { position: "start" },
                    }}
                  />
                </FormField>

                <FormField label="Appointment Time">
                  <TimePicker
                    value={timeValue}
                    onChange={handleTimeChange}
                    slotProps={{
                      textField: { fullWidth: true, sx: dateTimeStyles },
                      inputAdornment: { position: "start" },
                    }}
                  />
                </FormField>
              </div>

              <FormField label="Appointment Status">
                <Select
                  required
                  className="w-full"
                  sx={selectStyles}
                  value={apptStatus}
                  onChange={(event) => setApptStatus(event.target.value)}
                >
                  <MenuItem value={"Assigned"}>Scheduled</MenuItem>
                  <MenuItem value={"Overdue"}>Missed</MenuItem>
                  <MenuItem value={"Done"}>Complete</MenuItem>
                </Select>
              </FormField>

              <FormField label="Appointment Notes">
                <TextEditor value={apptNotes} onChangeHandler={onChangeHandlerEditNote} />
              </FormField>
            </div>
          </div>

          <div className="my-2 text-sm">
            <span className="font-bold text-red-600">Warning:</span> Clicking the "Update
            Appointment Details" button below will permanently rewrite over the current appointment
            data, after proceeding previous data will be unrecoverable. Please make sure you want
            the above changes to take effect before proceeding.
          </div>

          <ResponseModal
            title="Appointment Details Updated"
            description="Successfully Updated Appointment Details"
            openBool={openSuccess}
            setOpenBool={setOpenSuccess}
            handleCloseFunc={handleOpenSuccess}
          />
          <ResponseModal
            title="Error Updating Appointment Data"
            isError={true}
            openBool={openError}
            setOpenBool={setOpenError}
            errorMessage={errorArray}
          />
        </div>
      </LocalizationProvider>
    </Modal>
  );
}

EditApptv2.propTypes = {
  selectedAppt: PropTypes.object,
  caseInfo: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  proCliTrigger: PropTypes.bool,
  setProCliTrigger: PropTypes.func,
  onExpand: PropTypes.func,
};
